import React, {useRef, useEffect, MutableRefObject} from 'react';
import {CKEditor} from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Essentials,
  Heading,
  HeadingButtonsUI,
  ParagraphButtonUI,
  Paragraph,
  Font,
  FontSize,
  Bold,
  Italic,
  Code,
  List,
  Indent,
  IndentBlock,
  CodeBlock,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar, type EditorConfig
} from 'ckeditor5';

import {useField} from 'formik';

import 'ckeditor5/ckeditor5.css';

// Replace the InstanceType line with:
type EditorInstance = Awaited<ReturnType<typeof ClassicEditor.create>>;

interface Ckeditor5FieldProps {
  name: string;
  formikRef?: MutableRefObject<EditorInstance | null | undefined>;
  [key: string]: unknown;
}

const Ckeditor5Field: React.FC<Ckeditor5FieldProps> = ({name, formikRef, ...props}) => {
  const [field, meta, helpers] = useField(name);
  const {value} = field;
  const {setValue, setTouched} = helpers;
  const editorRef = useRef<EditorInstance | null>(null);
  
  useEffect(() => {
    if (formikRef && editorRef.current) {
      formikRef.current = editorRef.current;
    }
    
    return () => {
      if (formikRef) {
        formikRef.current = null;
      }
    };
  }, [formikRef]);
  
  const editorConfiguration : EditorConfig = {
    licenseKey: 'GPL',
    plugins: [
      Table,
      TableCellProperties,
      TableProperties,
      TableToolbar,
      Essentials,
      Heading,
      HeadingButtonsUI,
      ParagraphButtonUI,
      Paragraph,
      Font,
      FontSize,
      Bold,
      Italic,
      Code,
      List,
      Indent,
      IndentBlock,
      CodeBlock
    ],
    toolbar: {
      items: [
        'heading',
        '|',
        'fontSize',
        'fontColor',
        'fontBackgroundColor',
        '|',
        'bold',
        'italic',
        'code', // Inline code
        '|',
        'bulletedList',
        'numberedList',
        '|',
        'outdent',
        'indent',
        '|',
        'codeBlock',
        'insertTable',
        '|',
        'undo',
        'redo',
      ],
      shouldNotGroupWhenFull: true
    },
    heading: {
      options: [
        {model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph'},
        {model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1'},
        {model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2'},
        {model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3'}
      ]
    },
    table: {
      contentToolbar: [
        'tableColumn',
        'tableRow',
        'mergeTableCells',
        'tableProperties',
        'tableCellProperties'
      ]
    },
    // heading: { ... },
    // fontSize: { ... },
    // codeBlock: { ... },
  };
  
  return (
    <div className={`ckeditor-container ${meta.touched && meta.error ? 'is-invalid' : ''}`}>
      <CKEditor
        editor={ClassicEditor}
        config={editorConfiguration}
        data={value || ""}
        onReady={editor => {
          editorRef.current = editor as EditorInstance;
          console.log('CKEditor5 React Component is ready to use!', editor);
        }}
        onChange={(_event, editor) => {
          const data = editor.getData();
          setValue(data);
        }}
        onBlur={() => {
          setTouched(true);
        }}
        onError={(error, {willEditorRestart}) => {
          console.error('CKEditor Error:', error);
          if (willEditorRestart) {
            if (editorRef.current && editorRef.current.ui && editorRef.current.ui.view.toolbar) { // 6. Null check
              if (editorRef.current.ui.view.toolbar.element) {
                editorRef.current.ui.view.toolbar.element.remove();
              }
            }
          }
        }}
        {...props}
      />
      {meta.touched && meta.error ? (
        <div className="invalid-feedback" style={{display: 'block'}}>{meta.error}</div>
      ) : null}
    </div>
  );
};

export default Ckeditor5Field;